import { MobileSelect } from "@components/dropdowns";
import { FormattedPrice } from "@features/eventDisplayGA/types";
import {
  ChevronRightIcon,
  ShoppingCartIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { Badge, Card, Text } from "@components/theming";
import { PriceStatus } from "@utils/enums";
import { formatPrice, joinClassNames } from "@utils/helpers";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import MobileFlexi from "./mobileFlexi";
import { useLocale, useTranslations } from "next-intl";
import { useNotificationStore } from "@store/notificationStore";
import { useIsShopsite } from "@context/DomainContext";
import { format, parseISO } from "date-fns";

interface Props {
  price: FormattedPrice;
  putQtyInCart: (
    price: FormattedPrice,
    qty: string,
    isDynamic?: boolean
  ) => Promise<"success" | "failure" | { status: string; retry_after: number }>;
  selectedQuantities: {
    id: string;
    qty: number;
    qty_from_combo: number;
    actual_booked_qty: number;
  }[];
  isComboShowing?: boolean;
}

const MobileTicketDisplay = ({
  price,
  putQtyInCart,
  selectedQuantities,
  isComboShowing,
}: Props) => {
  const t = useTranslations("EventPage");
  const locale = useLocale();
  const setFailure = useNotificationStore((state) => state.setFailure);
  const selectedQty = selectedQuantities.find((sq) => sq.id === price.id);
  const [prevQuantity, setPrevQuantity] = useState<string>(
    selectedQty ? selectedQty.qty.toString() : "0"
  );
  // Tracks the quantity that was actually requested in the latest change so the
  // success message can be derived synchronously, rather than relying on the
  // async `selectedQty.qty` prop which may still be stale when the result renders.
  const [resultQty, setResultQty] = useState<string | null>(null);
  const [failedRetry, setFailedRetry] = useState(false);
  const [priceLoading, setPriceLoading] = useState<{
    id: number;
    status: "loading" | "success" | "failure";
  } | null>(null);
  const isShopsite = useIsShopsite();

  const isMaxComboShowingsBooked =
    isComboShowing && selectedQty
      ? selectedQty.actual_booked_qty >= (price.bookingLimit ?? price.max)
      : false;

  const comboQty = selectedQty?.qty_from_combo;

  // Determine the direction of the change from the requested quantity (known
  // synchronously) when available, falling back to the current selected qty.
  // This avoids a race where `selectedQty.qty` is still stale (e.g. reading 0
  // right after adding again post cart-clear), which previously made both sides
  // of the comparison equal and fell through to "something went wrong".
  const quantityDiff =
    (resultQty !== null ? parseInt(resultQty) : selectedQty?.qty ?? 0) -
    parseInt(prevQuantity);

  const option_array = [
    0,
    ...Array.from(
      {
        length:
          (price.bookingLimit ?? price.max) -
          price.min +
          (price.min === 0 ? 0 : 1) +
          (price.bookingLimit && price.bookingLimit < price.max && selectedQty
            ? selectedQty.qty
            : 0) -
          (isComboShowing && selectedQty?.actual_booked_qty
            ? selectedQty.actual_booked_qty
            : 0),
      },
      (_, index) => price.min + index + (price.min === 0 ? 1 : 0)
    ),
  ];

  async function handleFlexiValueChange(qty: string, dynamicePrice: number) {
    setPriceLoading({
      id: price.priceAgeId
        ? price.priceAgeId
        : price.comboId
        ? price.comboId
        : 0,
      status: "loading",
    });

    const newPrice = dynamicePrice || price.price;
    const newObjPrice = { ...price, price: newPrice };
    const result = await putQtyInCart(newObjPrice, qty, true);

    if (typeof result !== "string" && result.status === "retry") {
      setTimeout(
        () => handleFlexiValueChange(qty, dynamicePrice),
        result.retry_after * 1000
      );
    }

    if (result === "success" || result === "failure") {
      setResultQty(qty);
      setPriceLoading({
        id: price.priceAgeId
          ? price.priceAgeId
          : price.comboId
          ? price.comboId
          : 0,
        status: result,
      });
      setTimeout(() => {
        setPriceLoading(null);
        setPrevQuantity(qty);
        setResultQty(null);
      }, 1000);
    }
  }

  async function handleValueChange(qty: string, retry_attempts = 0) {
    if (retry_attempts > 5) {
      setPriceLoading({
        id: price.priceAgeId
          ? price.priceAgeId
          : price.comboId
          ? price.comboId
          : 0,
        status: "failure",
      });
      setFailedRetry(true);
      setTimeout(() => {
        setPriceLoading(null);
        setFailedRetry(false);
      }, 2500);
      setFailure(t("error.retryfailednotification"));
      return;
    }
    setPriceLoading({
      id: price.priceAgeId
        ? price.priceAgeId
        : price.comboId
        ? price.comboId
        : 0,
      status: "loading",
    });

    const result = await putQtyInCart(price, qty);

    if (typeof result !== "string" && result.status === "retry") {
      setTimeout(
        () => handleValueChange(qty, retry_attempts + 1),
        result.retry_after * 1000
      );
    }

    if (result === "success" || result === "failure") {
      setResultQty(qty);
      setPriceLoading({
        id: price.priceAgeId
          ? price.priceAgeId
          : price.comboId
          ? price.comboId
          : 0,
        status: result,
      });
      setTimeout(() => {
        setPriceLoading(null);
        setPrevQuantity(qty);
        setResultQty(null);
      }, 1000);
    }
  }

  // Ticket is unavailable for reasons unrelated to the current in-flight
  // request (sold out, booking limit, etc). Used to decide whether the tap
  // indicator/badge should be unmounted entirely (no reserved space) versus
  // just faded out while settling, so the card height doesn't jump around
  // during the loading/success/failure cycle.
  const isPermanentlyDisabled =
    (price.isCombo
      ? price.max === 0 || price.max === price.bookedTickets
      : (price.status !== PriceStatus.AVAILABLE && selectedQty?.qty === 0) ||
        (price.bookingLimit ?? price.max) < price.min) ||
    isMaxComboShowingsBooked;

  const isDisabled =
    (price.priceAgeId && priceLoading?.id === price.priceAgeId) ||
    (price.comboId && priceLoading?.id === price.comboId) ||
    isPermanentlyDisabled;

  if (
    price.priceMaximum !== null &&
    price.priceMaximum > 0 &&
    price.priceMinimum !== null &&
    price.priceMinimum > 0
  ) {
    return (
      <MobileFlexi
        price={price}
        priceLoading={priceLoading}
        quantity={selectedQty?.qty.toString()}
        prevQuantity={prevQuantity}
        onOptionSelect={handleFlexiValueChange}
        options={option_array}
      />
    );
  }

  return (
    <MobileSelect
      key={selectedQty?.actual_booked_qty.toString()}
      description={price.info}
      defaultValue={selectedQty?.qty.toString()}
      onOptionSelect={handleValueChange}
      options={option_array.map((v) => ({ label: v, value: v }))}
    >
      {() => (
        <div className="relative">
          <Card
            asChild
            size="2"
            sharedClasses={joinClassNames(
              "w-full relative flex flex-col gap-2",
              priceLoading?.status === "loading" ? "overflow-hidden" : ""
            )}
            whitelabelClasses="disabled:before:bg-[color:var(--gray-5)]"
            shopsiteClasses={joinClassNames(
              "px-4 py-2 border shadow rounded-xl transition-all duration-150",
              isDisabled
                ? "border-gray-300 bg-gray-100"
                : "bg-white border-gray-200"
            )}
          >
            <motion.button
              whileTap={
                !isDisabled
                  ? {
                      scale: 0.95,
                    }
                  : undefined
              }
              disabled={isDisabled}
            >
              <AnimatePresence>
                {priceLoading?.status === "loading" && (
                  <motion.div
                    className="absolute top-0 left-0 w-[200%] h-full"
                    style={{
                      backgroundImage: `repeating-linear-gradient( 105deg,#9ca3af44,
                        #9ca3af44 7%,
                        #4b556344 7%,
                        #4b556344 10%
                      )`,
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ x: ["0%", "-50%"], opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      repeat: Infinity,
                      ease: "linear",
                      duration: 3,
                      opacity: {
                        duration: 0.1,
                      },
                    }}
                  />
                )}
              </AnimatePresence>
              <AnimatePresence>
                {(priceLoading?.status === "success" ||
                  priceLoading?.status === "failure") &&
                  selectedQty && (
                    <motion.div
                      className={joinClassNames(
                        "absolute top-0 left-0 z-10 flex items-center justify-center w-full h-full rounded-xl",
                        priceLoading.status === "success"
                          ? isShopsite
                            ? "bg-green-100"
                            : "bg-[color:var(--green-3)]"
                          : isShopsite
                          ? "bg-red-100"
                          : "bg-[color:var(--red-3)]"
                      )}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {(quantityDiff > 0 || isComboShowing) &&
                      priceLoading.status === "success" ? (
                        <div
                          className={joinClassNames(
                            "flex items-center gap-2",
                            isShopsite
                              ? "text-green-800"
                              : "text-[color:var(--green-11)]"
                          )}
                        >
                          <ShoppingCartIcon className="h-7 w-7" />
                          <Text
                            as="span"
                            size="3"
                            weight="bold"
                            shopsiteClasses="text-lg font-semibold"
                          >
                            {t("gaselection.addedtocart")}
                          </Text>
                        </div>
                      ) : quantityDiff < 0 &&
                        priceLoading.status === "success" ? (
                        <div
                          className={joinClassNames(
                            "flex items-center gap-2",
                            isShopsite
                              ? "text-green-800"
                              : "text-[color:var(--green-11)]"
                          )}
                        >
                          <ShoppingCartIcon className="h-7 w-7" />
                          <Text
                            as="span"
                            size="3"
                            weight="bold"
                            shopsiteClasses="text-lg font-semibold"
                          >
                            {t("gaselection.removedfromcart")}
                          </Text>
                        </div>
                      ) : failedRetry ? (
                        <Text
                          as="span"
                          size="3"
                          weight="bold"
                          whitelabelClasses="text-[color:var(--red-11)]"
                          shopsiteClasses="text-red-800"
                        >
                          {t("error.retryfailedmessage")}
                        </Text>
                      ) : (
                        <Text
                          as="span"
                          size="3"
                          weight="bold"
                          whitelabelClasses="text-[color:var(--red-11)]"
                          shopsiteClasses="text-red-800"
                        >
                          {t("gaselection.somethingwentwrong")}
                        </Text>
                      )}
                    </motion.div>
                  )}
              </AnimatePresence>
              <div className="flex flex-row items-start justify-between gap-2 w-full">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1">
                  <Text
                    as="span"
                    color={isDisabled ? "gray" : undefined}
                    align="left"
                    size="4"
                    weight="bold"
                    shopsiteClasses={joinClassNames(
                      "text-left text-lg font-semibold",
                      isDisabled ? "text-gray-400" : ""
                    )}
                  >
                    {price.type}
                  </Text>
                  {isMaxComboShowingsBooked ? (
                    <Badge
                      color="red"
                      shopsiteClasses={joinClassNames(
                        "!rounded-lg font-semibold transition-opacity duration-150",
                        priceLoading?.status === "loading"
                          ? "opacity-0"
                          : "opacity-100"
                      )}
                    >
                      <Text size="1" weight="bold">
                        {t("gaselection.limitreached")}
                      </Text>
                    </Badge>
                  ) : null}
                  {price.isCombo ? (
                    price.max === 0 ? (
                      <Badge
                        color="red"
                        shopsiteClasses="!rounded-lg font-semibold"
                      >
                        <Text size="1" weight="bold">
                          {t("gaselection.soldout")}
                        </Text>
                      </Badge>
                    ) : price.max === price.bookedTickets ? (
                      <Badge
                        color="red"
                        shopsiteClasses="!rounded-lg font-semibold"
                      >
                        <Text size="1" weight="bold">
                          {t("gaselection.maxbought")}
                        </Text>
                      </Badge>
                    ) : null
                  ) : (
                    ((price.status !== PriceStatus.AVAILABLE &&
                      selectedQty?.qty === 0) ||
                      (price.bookingLimit ?? price.max) < price.min) && (
                      <>
                        {price.status === PriceStatus.UPCOMING ? (
                          <Badge
                            color="amber"
                            shopsiteClasses="!rounded-lg font-semibold"
                          >
                            <Text size="1" weight="bold">
                              {t("gaselection.notavailable")}
                            </Text>
                          </Badge>
                        ) : price.status === PriceStatus.AWAITINGTRIGGER ? (
                          <Badge
                            color="purple"
                            shopsiteClasses="!rounded-lg font-semibold"
                          >
                            <Text size="1" weight="bold">
                              {t("gaselection.awaitingtrigger")}
                            </Text>
                          </Badge>
                        ) : (
                          <Badge
                            color="red"
                            shopsiteClasses="!rounded-lg font-semibold"
                          >
                            <Text size="1" weight="bold">
                              {price.status === PriceStatus.NOTAVAILABLE
                                ? t("gaselection.notavailable")
                                : price.status === PriceStatus.SOLDOUT
                                ? t("gaselection.soldout")
                                : price.status === PriceStatus.BOOKEDOUT
                                ? t("gaselection.bookedout")
                                : t("gaselection.limitreached")}
                            </Text>
                          </Badge>
                        )}
                      </>
                    )
                  )}
                  {price.status === PriceStatus.UPCOMING &&
                  (price.availableFrom || price.priceAgeStartDate) ? (
                    <Badge
                      asChild
                      color="amber"
                      size="2"
                      sharedClasses="cursor-default"
                      shopsiteClasses="!rounded-lg truncate font-semibold w-fit"
                    >
                      <Text
                        as="span"
                        size="2"
                        weight="bold"
                        sharedClasses="w-fit truncate"
                      >
                        {t("gaselection.availability", {
                          preposition: "from",
                          date: format(
                            parseISO(
                              price.availableFrom ??
                                price.priceAgeStartDate ??
                                ""
                            ),
                            "EEE dd MMM yyy"
                          ),
                        })}
                      </Text>
                    </Badge>
                  ) : (
                    <></>
                  )}
                </div>
                {price.isCombo ? (
                  <div className="flex flex-col items-end h-7">
                    <Text
                      as="span"
                      color={isDisabled ? "gray" : undefined}
                      size="4"
                      weight="bold"
                      shopsiteClasses={joinClassNames(
                        "text-lg font-semibold",
                        isDisabled ? "text-gray-400" : "text-indigo-500"
                      )}
                    >
                      {formatPrice(
                        locale,
                        price.currency.CurrencyCode,
                        price.discountPrice
                      )}
                    </Text>
                    {price.price > price.discountPrice ? (
                      <Text
                        as="span"
                        size="2"
                        weight="medium"
                        color="gray"
                        sharedClasses="line-through"
                        shopsiteClasses="text-sm text-gray-400"
                      >
                        {formatPrice(
                          locale,
                          price.currency.CurrencyCode,
                          price.price
                        )}
                      </Text>
                    ) : (
                      <></>
                    )}
                  </div>
                ) : (
                  <Text
                    as="span"
                    color={isDisabled ? "gray" : undefined}
                    size="4"
                    weight="bold"
                    shopsiteClasses={joinClassNames(
                      "text-lg font-semibold",
                      isDisabled ? "text-gray-400" : "text-indigo-500"
                    )}
                  >
                    {formatPrice(
                      locale,
                      price.currency.CurrencyCode,
                      price.price
                    )}
                  </Text>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap w-full">
                {price.isCombo ? (
                  <Badge color="green" shopsiteClasses="!rounded-md w-fit">
                    <Text
                      size="1"
                      weight="bold"
                      shopsiteClasses="font-semibold text-xs"
                    >
                      {t("gaselection.combo")}
                    </Text>
                  </Badge>
                ) : (
                  <></>
                )}
                {price.areaName ? (
                  <Badge color="sky" shopsiteClasses="!rounded-md w-fit">
                    <Text
                      size="1"
                      weight="bold"
                      shopsiteClasses="font-semibold text-xs"
                    >
                      {price.areaName}
                    </Text>
                  </Badge>
                ) : (
                  <></>
                )}
                {price.priceSchemeName ? (
                  <Badge color="cyan" shopsiteClasses="!rounded-md w-fit">
                    <Text
                      size="1"
                      weight="bold"
                      shopsiteClasses="font-semibold text-xs"
                    >
                      {price.priceSchemeName}
                    </Text>
                  </Badge>
                ) : (
                  <></>
                )}
                {price.priceSectionName ? (
                  <Badge color="teal" shopsiteClasses="!rounded-md w-fit">
                    <Text
                      size="1"
                      weight="bold"
                      shopsiteClasses="font-semibold text-xs"
                    >
                      {price.priceSectionName}
                    </Text>
                  </Badge>
                ) : (
                  <></>
                )}
              </div>
              {/* Tap indicator - reserved space always, only visibility (opacity)
                  changes while settling, so the card height stays constant
                  through the loading/success/failure cycle. Fully unmounted
                  only when the ticket is permanently unavailable. */}
              {!isPermanentlyDisabled && (
                <div
                  className={joinClassNames(
                    "flex items-center justify-end gap-1 transition-opacity duration-150",
                    isDisabled ? "opacity-0" : "opacity-100"
                  )}
                >
                  <Text
                    as="span"
                    size="1"
                    whitelabelClasses="text-[color:var(--accent-9)]"
                    shopsiteClasses="text-xs font-medium text-indigo-500"
                  >
                    {t("gaselection.taptoadd")}
                  </Text>
                  <ChevronRightIcon
                    className={joinClassNames(
                      "h-4 w-4",
                      isShopsite
                        ? "text-indigo-500"
                        : "text-[color:var(--accent-9)]"
                    )}
                  />
                </div>
              )}
            </motion.button>
          </Card>
          <div
            className={joinClassNames(
              "absolute -top-2 -right-1.5 flex items-center gap-1 flex-wrap justify-end transition-opacity duration-500 ease-in-out ",
              priceLoading !== null ? "opacity-0" : "opacity-100"
            )}
          >
            <Badge
              variant="solid"
              sharedClasses={selectedQty?.qty === 0 ? "hidden" : ""}
              shopsiteClasses="!rounded-lg !gap-1 !text-xs"
            >
              <Text as="span" size="1" weight="medium">
                {t("amountincart", { amount: selectedQty?.qty ?? "" })}
              </Text>
            </Badge>
            <Badge
              variant="solid"
              color="green"
              sharedClasses={comboQty === 0 ? "hidden" : ""}
              shopsiteClasses="!rounded-lg !gap-1 !text-xs"
            >
              <Text as="span" size="1" weight="medium">
                {t("gaselection.amountfromcombo", { amount: comboQty ?? "" })}
              </Text>
            </Badge>
          </div>
          <motion.div
            className="absolute -top-2 -right-1 z-20"
            animate={
              priceLoading?.status !== "loading"
                ? { scale: [0, 1, 1.2, 1, 0], rotate: [0, -3, -6, -9, -12] }
                : { scale: 0, rotate: 0 }
            }
            transition={{ duration: 1.5 }}
          >
            {priceLoading?.status === "success" ? (
              <SparklesIcon className="text-yellow-400 h-6 w-6" />
            ) : priceLoading?.status === "failure" && !failedRetry ? (
              <XMarkIcon className="text-red-400 h-6 w-6" />
            ) : (
              <></>
            )}
          </motion.div>
          <motion.div
            className="absolute -bottom-2 -left-1 z-20"
            animate={
              priceLoading?.status !== "loading"
                ? { scale: [0, 1, 1.2, 1, 0], rotate: [0, -3, -6, -9, -12] }
                : { scale: 0, rotate: 0 }
            }
            transition={{ duration: 1.5 }}
          >
            {priceLoading?.status === "success" ? (
              <SparklesIcon className="text-yellow-400 h-6 w-6" />
            ) : priceLoading?.status === "failure" && !failedRetry ? (
              <XMarkIcon className="text-red-400 h-6 w-6" />
            ) : (
              <></>
            )}
          </motion.div>
          {priceLoading && priceLoading.status !== "loading" && !failedRetry ? (
            <>
              <motion.div
                className={joinClassNames(
                  "absolute -top-2 left-4 w-2.5 h-2.5 rounded-[2px] z-20",
                  priceLoading.status === "success"
                    ? "bg-emerald-200"
                    : "bg-red-200"
                )}
                initial={{ x: 0, y: 0, opacity: 0, rotate: 0 }}
                animate={{
                  x: [0, -4, -8, -14, -16, -18],
                  y: [0, -8, -10, -9, -5, 10],
                  opacity: [0, 1, 1, 1, 1, 0],
                  rotate: [-1, -3, -6, -9, -12],
                }}
                transition={{ duration: 1, delay: 0.1 }}
              />
              <motion.div
                className={joinClassNames(
                  "absolute -top-2 left-24 w-3.5 h-3.5 rounded z-20",
                  priceLoading.status === "success"
                    ? "bg-emerald-500"
                    : "bg-red-500"
                )}
                initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                animate={{
                  x: [0, -2, -4, -5, -5],
                  y: [0, -2, -4, -5, -5],
                  scale: [0, 1, 1.2, 1, 0],
                  rotate: [0, -3, -6, -9, -12],
                }}
                transition={{ duration: 1.2 }}
              />
              <motion.div
                className={joinClassNames(
                  "absolute -top-4 right-20 w-5 h-5 rounded-[3px] z-20",
                  priceLoading.status === "success"
                    ? "bg-emerald-200"
                    : "bg-red-700"
                )}
                initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                animate={{
                  x: [0, 2, 4, 5, 5],
                  y: [0, -2, -4, -5, -7, -7],
                  scale: [0, 1, 1.2, 1, 0],
                  rotate: [1, 6, 12, 18, 24],
                }}
                transition={{ duration: 1.5, delay: 0.2 }}
              />
              <motion.div
                className={joinClassNames(
                  "absolute -bottom-1 right-1/2 w-3.5 h-3.5 rounded-[3px] z-20",
                  priceLoading.status === "success"
                    ? "bg-emerald-500"
                    : "bg-red-500"
                )}
                initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                animate={{
                  x: [0, -2, -4, -5, -5],
                  y: [0, 2, 4, 5, 7, 7],
                  scale: [0, 1, 1.2, 0.8, 0.4, 0],
                  rotate: [0, -3, -6, -9, -12],
                }}
                transition={{ duration: 1.5 }}
              />
              <motion.div
                className={joinClassNames(
                  "absolute -bottom-2 right-4 w-4 h-4 rounded-[3px] z-20",
                  priceLoading.status === "success"
                    ? "bg-emerald-500"
                    : "bg-red-500"
                )}
                initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                animate={{
                  x: [0, 1, 2, 3, 3],
                  y: [0, 2, 4, 5, 5],
                  scale: [0, 1, 1.2, 1, 0],
                  rotate: [0, -3, -6, -9, -12],
                }}
                transition={{ duration: 1.5 }}
              />
            </>
          ) : (
            <></>
          )}
        </div>
      )}
    </MobileSelect>
  );
};

export default MobileTicketDisplay;
